/**
 * Hook do PLD médio ANUAL (Task #225/#29 — consome /api/v2/pld/anual, derivado do
 * canonical_pld_mensal, 2001+). Cache keyed por submercado. TTL 5 min.
 *
 * `usePldAnualAll` chama 4× (SE/S/NE/N) pra sub-tab Histórico (visão anual);
 * combinação client-side porque o endpoint é por submercado. Anual = média
 * aritmética dos meses do ano; ano incompleto vem com `completo=false`.
 */
import { useEffect, useState } from "react";
import { getPldAnual } from "../api/client";
import type { PldAnualResponse, PldSubmercado } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 5 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<PldAnualResponse>;
}

const cache = new Map<string, CacheEntry>();

export function invalidatePldAnualCache(): void {
  cache.clear();
}

export interface UsePldAnualState {
  data: PldAnualResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function usePldAnual(submercado: string): UsePldAnualState {
  const [state, setState] = useState<UsePldAnualState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    const key = submercado;

    function load() {
      const now = Date.now();
      let entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        entry = { expiresAt: now + REFETCH_MS, promise: getPldAnual(submercado) };
        cache.set(key, entry);
      }
      entry.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [submercado]);

  return state;
}

export interface UsePldAnualAllState {
  bySubmercado: Record<PldSubmercado, PldAnualResponse | null>;
  isLoading: boolean;
  error: Error | null;
}

/** PLD anual das 4 submercados (2001+) — visão anual do Histórico. */
export function usePldAnualAll(): UsePldAnualAllState {
  const se = usePldAnual("SE");
  const s = usePldAnual("S");
  const ne = usePldAnual("NE");
  const n = usePldAnual("N");
  return {
    bySubmercado: { SE: se.data, S: s.data, NE: ne.data, N: n.data },
    isLoading: se.isLoading || s.isLoading || ne.isLoading || n.isLoading,
    error: se.error ?? s.error ?? ne.error ?? n.error,
  };
}
